import {
  Component,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { AIChatbotService } from '../../services/ai-chatbot/ai-chatbot.service';
import { Subscription } from 'rxjs';
import { AIChatbotMessage } from '../../models/ai-chatbot-message';

@Component({
  selector: 'ai-chatbot-chat-scroll',
  templateUrl: './ai-chatbot-chat-scroll.component.html',
  styleUrl: './ai-chatbot-chat-scroll.component.scss',
  standalone: false,
})
export class AiChatbotChatScrollComponent implements AfterViewInit, OnDestroy {
  @ViewChild('chatScroll', { static: false }) chatScroll!: ElementRef;

  @Input() messages: AIChatbotMessage[] = [];
  @Input() isProcessing: boolean = false;
  @Input() isLoadingTemplates: boolean = false;
  @Output() selectedTemplateIdxChange = new EventEmitter<number>();

  private scrollChatToBottomSubscription: Subscription;
  private scrollbarHoverHandler?: (e: MouseEvent) => void;
  private scrollbarMouseleaveHandler?: () => void;
  private scrollbarIdleHandlers?: {
    scroll: () => void;
    mouseenter: () => void;
    mousemove: (e: MouseEvent) => void;
    mouseleave: () => void;
  };
  private scrollbarIdleTimeout?: any;

  constructor(
    private aiChatbotService: AIChatbotService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    this.setupScrollbarHover();
    this.setupScrollbarIdle();
    this.subscribeToScrollChat();
    this.scrollChatToBottom();
  }

  ngOnDestroy(): void {
    if (this.scrollChatToBottomSubscription) {
      this.scrollChatToBottomSubscription.unsubscribe();
      this.scrollChatToBottomSubscription = null;
    }
    this.cleanupScrollbarHandlers();
  }

  private subscribeToScrollChat(): void {
    if (this.scrollChatToBottomSubscription) {
      this.scrollChatToBottomSubscription.unsubscribe();
    }

    this.scrollChatToBottomSubscription =
      this.aiChatbotService.scrollChatToBottom$.subscribe(
        (options: { onlyIfAtBottom?: boolean }) => {
          this.scrollChatToBottom(options.onlyIfAtBottom);
        },
      );
  }

  private cleanupScrollbarHandlers(): void {
    if (!this.chatScroll?.nativeElement) {
      return;
    }

    const scrollElement = this.chatScroll.nativeElement;

    if (this.scrollbarHoverHandler) {
      scrollElement.removeEventListener(
        'mousemove',
        this.scrollbarHoverHandler,
      );
      this.scrollbarHoverHandler = undefined;
    }

    if (this.scrollbarMouseleaveHandler) {
      scrollElement.removeEventListener(
        'mouseleave',
        this.scrollbarMouseleaveHandler,
      );
      this.scrollbarMouseleaveHandler = undefined;
    }

    if (this.scrollbarIdleHandlers) {
      scrollElement.removeEventListener(
        'scroll',
        this.scrollbarIdleHandlers.scroll,
      );
      scrollElement.removeEventListener(
        'mouseenter',
        this.scrollbarIdleHandlers.mouseenter,
      );
      scrollElement.removeEventListener(
        'mousemove',
        this.scrollbarIdleHandlers.mousemove,
      );
      scrollElement.removeEventListener(
        'mouseleave',
        this.scrollbarIdleHandlers.mouseleave,
      );
      this.scrollbarIdleHandlers = undefined;
    }

    if (this.scrollbarIdleTimeout) {
      clearTimeout(this.scrollbarIdleTimeout);
      this.scrollbarIdleTimeout = undefined;
    }
  }

  private setupScrollbarHover(): void {
    if (!this.chatScroll?.nativeElement) return;

    const scrollElement = this.chatScroll.nativeElement;
    const scrollbarWidth = 15;

    this.scrollbarHoverHandler = (e: MouseEvent) => {
      const rect = scrollElement.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const elementWidth = rect.width;

      if (mouseX > elementWidth - scrollbarWidth) {
        scrollElement.classList.add('scrollbar-hovered');
      } else {
        scrollElement.classList.remove('scrollbar-hovered');
      }
    };

    this.scrollbarMouseleaveHandler = () => {
      scrollElement.classList.remove('scrollbar-hovered');
    };

    scrollElement.addEventListener('mousemove', this.scrollbarHoverHandler);
    scrollElement.addEventListener(
      'mouseleave',
      this.scrollbarMouseleaveHandler,
    );
  }

  private setupScrollbarIdle(): void {
    if (!this.chatScroll?.nativeElement) return;

    const scrollElement = this.chatScroll.nativeElement;
    const idleTime = 1500;
    const scrollbarWidth = 15;
    let isHoveringScrollbar = false;

    const showScrollbar = () => {
      scrollElement.classList.remove('scrollbar-idle');

      if (this.scrollbarIdleTimeout) {
        clearTimeout(this.scrollbarIdleTimeout);
      }

      this.scrollbarIdleTimeout = setTimeout(() => {
        if (!isHoveringScrollbar) {
          scrollElement.classList.add('scrollbar-idle');
        }
      }, idleTime);
    };

    const handleScroll = () => showScrollbar();
    const handleMouseEnter = () => showScrollbar();
    const handleMouseMove = (e: MouseEvent) => {
      const rect = scrollElement.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const elementWidth = rect.width;

      isHoveringScrollbar = mouseX > elementWidth - scrollbarWidth;

      showScrollbar();
    };
    const handleMouseLeave = () => {
      isHoveringScrollbar = false;
      if (this.scrollbarIdleTimeout) {
        clearTimeout(this.scrollbarIdleTimeout);
      }
      scrollElement.classList.add('scrollbar-idle');
    };

    this.scrollbarIdleHandlers = {
      scroll: handleScroll,
      mouseenter: handleMouseEnter,
      mousemove: handleMouseMove,
      mouseleave: handleMouseLeave,
    };

    scrollElement.addEventListener('scroll', handleScroll);
    scrollElement.addEventListener('mouseenter', handleMouseEnter);
    scrollElement.addEventListener('mousemove', handleMouseMove);
    scrollElement.addEventListener('mouseleave', handleMouseLeave);

    scrollElement.classList.add('scrollbar-idle');
  }

  scrollChatToBottom = (onlyIfAtBottom: boolean = false) => {
    if (this.chatScroll) {
      setTimeout(() => {
        const element = this.chatScroll.nativeElement;

        if (onlyIfAtBottom) {
          const isAtBottom =
            element.scrollTop + element.clientHeight >=
            element.scrollHeight - 10;
          if (!isAtBottom) return;
        }

        element.scrollTo({
          top: element.scrollHeight,
          behavior: 'smooth',
        });
        this.cdr.detectChanges();
      });
    }
  };
}
